import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { noteApi } from '../utils/api';
import { useTranslation } from 'react-i18next';
import { Card, CardBody, Form, FormGroup, Label, Input, Button, Badge, Spinner, ListGroup, ListGroupItem, Alert } from 'reactstrap';
import { ArrowLeft, Upload, Paperclip, Trash2, Save, X, Lock } from 'lucide-react';
import { getShareById, deleteShareById, getLatestPendingShare, cleanOldShares } from '../utils/shareDb';
import { parseSharedPayload } from '../utils/shareParser';

import DateTimePicker from '../components/DateTimePicker';

const NewNote = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { t } = useTranslation();
    
    const [note, setNote] = useState({ title: '', content: '', tags: [], targetDate: '', confidential: false });
    const [files, setFiles] = useState([]);
    const [tagInput, setTagInput] = useState('');
    const [saving, setSaving] = useState(false);
    const [loadingShare, setLoadingShare] = useState(false);
    const [shareNotice, setShareNotice] = useState(null);

    useEffect(() => {
        const loadSharedData = async () => {
            const shareId = searchParams.get('shareId');
            const fromShare = searchParams.get('fromShare');
            
            let sharedItem = null;
            if (shareId) {
                setLoadingShare(true);
                sharedItem = await getShareById(shareId);
                if (sharedItem) {
                    await deleteShareById(shareId);
                }
            } else if (fromShare) {
                setLoadingShare(true);
                sharedItem = await getLatestPendingShare();
                if (sharedItem) {
                    await deleteShareById(sharedItem.id);
                }
            }

            if (!sharedItem && (searchParams.get('title') || searchParams.get('text') || searchParams.get('url'))) {
                sharedItem = {
                    title: searchParams.get('title') || '',
                    text: searchParams.get('text') || '',
                    url: searchParams.get('url') || '',
                    files: []
                };
            }

            if (sharedItem) {
                const parsed = parseSharedPayload(sharedItem);
                if (parsed.hasSharedData) {
                    setNote(prev => ({
                        ...prev,
                        title: parsed.title || prev.title,
                        content: parsed.content || prev.content,
                        tags: Array.from(new Set([...prev.tags, ...parsed.tags]))
                    }));
                    if (parsed.files && parsed.files.length > 0) {
                        setFiles(prev => [...prev, ...parsed.files]);
                    }
                    setShareNotice(t('shared_content_imported'));
                }
            }
            setLoadingShare(false);
            cleanOldShares();
        };

        loadSharedData();
    }, [searchParams, t]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const noteToSave = { 
                ...note, 
                targetDate: note.targetDate ? note.targetDate : null 
            };
            
            const res = await noteApi.post('', noteToSave);
            const noteId = res.data.id;

            if (files.length > 0) {
                for (const file of files) {
                    const formData = new FormData();
                    formData.append('file', file);
                    await noteApi.post(`${noteId}/attachments`, formData);
                }
            }

            window.dispatchEvent(new CustomEvent('notes-updated'));
            navigate('/dashboard');
        } catch (err) {
            console.error("Creation failed", err);
            alert(t('error_creating'));
        } finally {
            setSaving(false);
        }
    };

    const handleAddTag = (e) => {
        if (e.key === 'Enter' || e.key === ',') {
            e.preventDefault();
            commitTag();
        }
    };

    const commitTag = () => {
        const tag = tagInput.trim().replace(',', '');
        if (tag && !note.tags.includes(tag)) {
            setNote(prev => ({ ...prev, tags: [...prev.tags, tag] }));
        }
        setTagInput('');
    };

    const removeTag = (tagToRemove) => {
        setNote({ ...note, tags: note.tags.filter(t => t !== tagToRemove) });
    };

    const handleFileChange = (e) => {
        const newFiles = Array.from(e.target.files);
        setFiles(prev => [...prev, ...newFiles]);
    };

    const removeFile = (index) => {
        setFiles(files.filter((_, i) => i !== index));
    };

    return (
        <div className="container mt-4 pb-5 animate-fade-in" style={{ maxWidth: '800px' }}>
            <div className="d-flex align-items-center mb-4">
                <Button
                    color="light"
                    onClick={() => navigate('/dashboard')}
                    className="rounded-pill px-3 py-2 border shadow-sm small me-3 btn-theme-aware d-inline-flex align-items-center gap-2 fw-semibold"
                >
                    <ArrowLeft size={16} />
                    <span>{t('back_button')}</span>
                </Button>
                <h2 className="fw-bold mb-0">{t('new_note_title')}</h2>
                {loadingShare && (
                    <span className="ms-3 text-muted small d-flex align-items-center">
                        <Spinner size="sm" color="primary" className="me-2" />
                        {t('loading_shared_content')}
                    </span>
                )}
            </div>

            {shareNotice && (
                <Alert color="info" toggle={() => setShareNotice(null)} className="rounded-4 border-0 shadow-sm mb-4">
                    {shareNotice}
                </Alert>
            )}

            <Card className="border-0 shadow-sm rounded-4 bg-card-custom no-hover-card">
                <CardBody className="p-4">
                    <Form onSubmit={handleSubmit}>
                        <FormGroup className="mb-4">
                            <Label className="small fw-bold opacity-75 text-uppercase">{t('title_placeholder')}</Label>
                            <Input 
                                type="text" 
                                className="form-control-lg border-custom bg-custom rounded-3 fw-bold"
                                value={note.title} 
                                onChange={e => setNote({ ...note, title: e.target.value })} 
                                placeholder={t('title_input_placeholder')}
                                required
                            />
                        </FormGroup>

                        <FormGroup className="mb-4">
                            <div className="p-3 rounded-3 border border-custom bg-custom d-flex flex-wrap justify-content-between align-items-center gap-2">
                                <div className="d-flex flex-column">
                                    <Label className="form-check-label fw-bold mb-1 d-inline-flex align-items-center gap-2 text-warning" htmlFor="newNoteConfidentialSwitch" style={{ cursor: 'pointer' }}>
                                        <Lock size={16} />
                                        <span>{t('mark_as_confidential')}</span>
                                    </Label>
                                    <small className="text-muted">{t('confidential_hint')}</small>
                                </div>
                                <div className="form-check form-switch mb-0">
                                    <Input
                                        type="checkbox"
                                        className="form-check-input"
                                        role="switch"
                                        id="newNoteConfidentialSwitch"
                                        style={{ cursor: 'pointer', width: '2.5em', height: '1.25em' }}
                                        checked={note.confidential}
                                        onChange={e => setNote({ ...note, confidential: e.target.checked })}
                                    />
                                </div>
                            </div>
                        </FormGroup>

                        <FormGroup className="mb-4">
                            <Label className="small fw-bold opacity-75 text-uppercase">{t('content_placeholder')}</Label>
                            <Input 
                                type="textarea" 
                                className="border-custom bg-custom rounded-3"
                                style={{ minHeight: '250px', resize: 'vertical' }}
                                value={note.content} 
                                onChange={e => setNote({ ...note, content: e.target.value })} 
                                placeholder={t('content_input_placeholder')}
                            />
                        </FormGroup>

                        <FormGroup className="mb-4">
                            <Label className="small fw-bold opacity-75 text-uppercase">{t('tags_placeholder')}</Label>
                            <div className="d-flex flex-wrap gap-2 mb-2">
                                {note.tags.map(tag => (
                                    <Badge key={tag} color="primary" pill className="px-3 py-2 d-inline-flex align-items-center gap-1 shadow-sm">
                                        <span>#{tag}</span>
                                        <X size={14} style={{ cursor: 'pointer' }} onClick={() => removeTag(tag)} />
                                    </Badge>
                                ))}
                            </div>
                            <Input 
                                type="text" 
                                className="border-custom bg-custom rounded-3"
                                value={tagInput}
                                onChange={e => setTagInput(e.target.value)}
                                onKeyDown={handleAddTag}
                                onBlur={commitTag}
                                placeholder={t('tag_input_placeholder')}
                            />
                        </FormGroup>

                        <FormGroup className="mb-4">
                            <Label className="small fw-bold opacity-75 text-uppercase">{t('deadline_optional')}</Label>
                            <DateTimePicker 
                                value={note.targetDate}
                                onChange={e => setNote({ ...note, targetDate: e.target.value })}
                            />
                        </FormGroup>

                        <FormGroup className="mb-4">
                            <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
                                <Label className="small fw-bold opacity-75 text-uppercase mb-0 d-flex align-items-center gap-1">
                                    <Paperclip size={16} />
                                    <span>{t('attachments')} ({files.length})</span>
                                </Label>
                                <div>
                                    <Input type="file" multiple onChange={handleFileChange} className="d-none" id="file-upload" />
                                    <label htmlFor="file-upload" className="btn btn-outline-primary btn-sm rounded-pill px-3 py-2 shadow-sm fw-bold mb-0 d-inline-flex align-items-center gap-2" style={{ cursor: 'pointer' }}>
                                        <Upload size={16} />
                                        <span>{files.length > 0 ? t('add_more_files') : t('choose_files')}</span>
                                    </label>
                                </div>
                            </div>
                            {files.length > 0 && (
                                <ListGroup className="rounded-3 border-custom shadow-sm overflow-hidden">
                                    {files.map((file, index) => (
                                        <ListGroupItem key={index} className="d-flex flex-wrap justify-content-between align-items-center gap-2 bg-custom border-custom small text-theme py-2 px-3">
                                            <span className="d-inline-flex align-items-center gap-2 text-truncate">
                                                <Paperclip size={15} className="text-primary flex-shrink-0" />
                                                <span className="fw-semibold text-truncate">{file.name}</span>
                                            </span>
                                            <Button
                                                color="outline-danger"
                                                size="sm"
                                                className="rounded-pill px-3 py-1 d-inline-flex align-items-center gap-1 fw-semibold"
                                                onClick={() => removeFile(index)}
                                            >
                                                <Trash2 size={14} />
                                                <span>{t('delete')}</span>
                                            </Button>
                                        </ListGroupItem>
                                    ))}
                                </ListGroup>
                            )}
                        </FormGroup>

                        <div className="d-grid mt-5">
                            <Button color="primary" size="lg" className="rounded-pill fw-bold shadow-sm py-3 d-inline-flex align-items-center justify-content-center gap-2" disabled={saving}>
                                {saving ? (
                                    <>
                                        <Spinner size="sm" />
                                        <span>{t('saving')}...</span>
                                    </>
                                ) : (
                                    <>
                                        <Save size={19} />
                                        <span>{t('create_note_button')}</span>
                                    </>
                                )}
                            </Button>
                        </div>
                    </Form>
                </CardBody>
            </Card>

            <style>{`
                .bg-card-custom { background-color: var(--sb-card-bg)!important; }
                .bg-custom { background-color: var(--sb-bg)!important; }
                .border-custom { border-color: var(--sb-border)!important; }
                .text-theme { color: var(--sb-text)!important; }
                .btn-theme-aware { 
                    background-color: var(--sb-card-bg)!important; 
                    color: var(--sb-text)!important; 
                    border-color: var(--sb-border)!important; 
                }
                .no-hover-card:hover {
                    transform: none !important;
                }
            `}</style>
        </div>
    );
};

export default NewNote;
